import { useStore } from '@/store/useStore';

/** Generate a structured clinical progress note from current session (PSD §8.2 / TODO Task 2.20). */
export function generateProgressNote(): string {
  const s = useStore.getState();
  const p = s.patient;
  const dw = s.dosingWeight;
  const renal = s.renal;
  const regimen = s.regimen;
  const safety = s.safety;
  const monitoring = s.monitoring;

  if (!p || !dw || !renal || !regimen) return 'Insufficient data to generate note.';

  const lines: string[] = [];
  lines.push('AMINOGLYCOSIDE DOSING NOTE');
  lines.push('='.repeat(40));
  lines.push('');
  lines.push('Patient:');
  lines.push(`  ${p.ageYears}y ${p.sex}, ${p.heightCm}cm, ${p.actualWeightKg}kg`);
  lines.push(`  SCr: ${p.scrMgDl} mg/dL  Indication: ${p.indication}`);
  lines.push('');
  lines.push('Derived values:');
  lines.push(`  IBW: ${dw.ibwKg.toFixed(1)}kg  AdjBW: ${dw.adjbwKg.toFixed(1)}kg  ABW: ${dw.abwKg.toFixed(1)}kg`);
  lines.push(`  Dosing weight (${dw.selectionRule}): ${dw.selectedWeightKg.toFixed(1)}kg`);
  lines.push(`  CrCl: ${renal.crClMlMin.toFixed(1)} mL/min  EI: ${renal.eiEligibility}`);
  lines.push(`  k_el: ${renal.populationKelPerHr.toFixed(4)} hr⁻¹`);
  lines.push('');
  lines.push('Regimen:');
  const dose = regimen.roundedDoseMg ?? regimen.empiricDoseMg;
  const interval = regimen.eiInterval ?? regimen.empiricIntervalHr;
  if (dose == null || interval == null) {
    lines.push('  [Dose or interval not yet calculated]');
  } else {
    lines.push(`  ${regimen.agent} ${dose.toFixed(0)}mg q${interval}h (${regimen.strategy})`);
    lines.push(`  Confidence: ${regimen.confidence}`);
    if (regimen.predictedPeakMg != null) lines.push(`  Predicted peak: ${regimen.predictedPeakMg.toFixed(1)} mg/L`);
    if (regimen.predictedTroughMg != null) lines.push(`  Predicted trough: ${regimen.predictedTroughMg.toFixed(1)} mg/L`);
    lines.push(`  Rationale: ${regimen.rationaleText}`);
  }
  lines.push('');
  if (safety && (safety.contraindications.length > 0 || safety.toxicityRisk !== 'low')) {
    lines.push('Safety:');
    lines.push(`  Toxicity risk: ${safety.toxicityRisk}`);
    if (safety.contraindications.length > 0) {
      lines.push(`  Contraindications: ${safety.contraindications.map((c) => c.type).join(', ')}`);
    }
    lines.push('');
  }
  if (monitoring) {
    lines.push('Monitoring:');
    lines.push(`  Pathway: ${monitoring.monitoringPathway}`);
    lines.push(`  SCr/BUN: q${monitoring.scrBunFrequencyDays}d`);
    if (monitoring.samplingSchedule.length > 0) {
      lines.push(`  Sampling: ${monitoring.samplingSchedule.map((sm) => `${sm.label} @ ${sm.offsetFromInfusionEndMin}min`).join('; ')}`);
    }
    lines.push('');
  }
  lines.push('Disclaimer: This is clinical decision support. All recommendations');
  lines.push('require review and approval by a qualified clinician.');
  lines.push(`Generated: ${new Date().toISOString()}`);

  return lines.join('\n');
}
